export const ssoProviders = ['microsoft', 'google'] as const;
export type SsoProvider = (typeof ssoProviders)[number];

export type GetSsoProviderLoginUrlRequest = {
  ssoProvider: SsoProvider;
  afterSuccessRedirectUrl: string; // The URL where the user will be redirected after a successful login via SSO
  shouldForceNewLoginAtSsoProvider?: boolean;
};

export type GetSsoProviderLoginUrlResponse = {
  ssoProviderLoginUrl: string; // The URL of the external SSO provider where the user is redirected to perform SSO
};

export enum SsoErrorType {
  OPEN_ID = 'OPEN_ID',
  LOGIN_ATTEMPT_EXPIRED = 'LOGIN_ATTEMPT_EXPIRED',
  ORGANIZATION_MISMATCH = 'ORGANIZATION_MISMATCH',
  EMAIL_MISSING_ON_USER_CREATION = 'EMAIL_MISSING_ON_USER_CREATION',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  NEW_LOGIN_REQUIRED = 'NEW_LOGIN_REQUIRED',
  OTHER = 'OTHER',
}

export type SsoErrorPayload =
  | SsoOpenIdErrorPayload
  | SsoLoginAttemptExpiredPayload
  | SsoOrganizationMismatchErrorPayload
  | SsoUserNotFoundErrorPayload
  | SsoEmailMissingOnUserCreationPayload
  | SsoEmailNotVerifiedPayload
  | SsoReLoginNeededErrorPayload
  | SsoOtherErrorPayload;

export type SsoErrorPayloadBase = {
  ssoLoginAttemptId: string | null;
  ssoProvider: SsoProvider;
  claims?: object;
};

type SsoOpenIdErrorPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.OPEN_ID;
  // TODO specify what checks actually didn't pass
};

type SsoLoginAttemptExpiredPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.LOGIN_ATTEMPT_EXPIRED;
};

type SsoOrganizationMismatchErrorPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.ORGANIZATION_MISMATCH;
  ssoOrganizationId: string;
};

type SsoEmailMissingOnUserCreationPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.EMAIL_MISSING_ON_USER_CREATION;
};

type SsoEmailNotVerifiedPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.EMAIL_NOT_VERIFIED;
};

type SsoUserNotFoundErrorPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.USER_NOT_FOUND;
  email: string | null;
  ssoServiceUserId: string | null;
};

type SsoOtherErrorPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.OTHER;
  message: string;
};

// Sent along when a login attempt is missing a refresh token
type SsoReLoginNeededErrorPayload = SsoErrorPayloadBase & {
  type: SsoErrorType.NEW_LOGIN_REQUIRED;
  afterSuccessRedirectUrl: string;
};

export type SsoProviderOidcRefreshData = {
  encryptedRefreshToken: string;
  lastRefreshAt: string;
};

// Sent along when any API request fails the refresh
export type SsoRefreshErrorPayload = {
  shouldRequestNewSso: true;
  ssoProvider: SsoProvider;
};
